import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { RemontaOrdemServico } from "@shared/schema";
import { ACTIVE_SERVICE_TYPES, type ActiveServiceType as ServiceType } from "@shared/pre-analise/types";
import { api } from "@shared/routes";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { addServiceBlockReason, availableServiceTypes } from "@/lib/dossier-web";
import { cn } from "@/lib/utils";
import { Loader2, Plus } from "lucide-react";
import { SERVICE_LABELS } from "./dossie-form";

const ALL_SERVICES = ACTIVE_SERVICE_TYPES;

/** Solicita o próximo serviço do Dossiê. Modalidades usadas aparecem, mas bloqueadas; a regra final é do servidor. */
export function AdicionarServicoDialog({ dossieId, orders, archived, hasVehicleData, recommendedTypes = [] }: {
  dossieId: string; orders: RemontaOrdemServico[]; archived: boolean; hasVehicleData: boolean; recommendedTypes?: readonly string[];
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState<ServiceType | "">("");
  const submitting = useRef(false);
  const blockReason = addServiceBlockReason({ arquivadoEm: archived ? "arquivado" : null }, orders, { hasVehicleData }) ?? (recommendedTypes.length ? null : "A pré-análise inicial precisa indicar um serviço viável antes da contratação.");
  const available = new Set(availableServiceTypes(orders, { hasVehicleData }).filter((service) => recommendedTypes.includes(service)));
  const used = new Map(orders.map((order) => [order.tipo, order.status]));

  const create = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/remonta/dossies/${dossieId}/ordens-servico`, { tipo })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.remontaOrdens.list.path, dossieId] });
      toast({ title: "Serviço solicitado", description: SERVICE_LABELS[tipo as ServiceType] });
      setOpen(false);
      setTipo("");
    },
    onError: (error: Error) => {
      const raw = error.message.replace(/^\d+:\s*/, "");
      let message = raw;
      try { message = JSON.parse(raw).message ?? raw; } catch { /* texto simples */ }
      toast({ title: "Não foi possível solicitar o serviço", description: message, variant: "destructive" });
    },
    onSettled: () => { submitting.current = false; },
  });

  const reasonFor = (service: ServiceType) => {
    if (!recommendedTypes.includes(service)) return "Sem indicação favorável na pré-análise inicial";
    if (used.has(service)) return used.get(service) === "cancelado" ? "Já solicitado e cancelado" : "Já solicitado";
    if (service === "reclassificacao_monta" && !hasVehicleData) return "Exige veículo com dados completos";
    return null;
  };

  return (
    <div className="space-y-1.5">
      <Button onClick={() => setOpen(true)} disabled={!!blockReason} aria-describedby={blockReason ? "adicionar-servico-motivo" : undefined}>
        <Plus className="mr-2 h-4 w-4" />Adicionar serviço
      </Button>
      {blockReason && <p id="adicionar-servico-motivo" className="text-sm text-muted-foreground">{blockReason}</p>}
      <Dialog open={open} onOpenChange={(value) => { if (!create.isPending) setOpen(value); }}>
        <DialogContent className="max-w-lg" style={{ width: "calc(100vw - 2rem)" }}>
          <DialogHeader>
            <DialogTitle>Adicionar serviço</DialogTitle>
            <DialogDescription>Cada modalidade pode ser solicitada uma vez por Dossiê, e só um serviço fica em andamento por vez.</DialogDescription>
          </DialogHeader>
          <fieldset className="space-y-2">
            <legend className="sr-only">Modalidade</legend>
            {ALL_SERVICES.map((service) => {
              const reason = reasonFor(service);
              const enabled = available.has(service);
              return (
                <label key={service} className={cn("flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm",
                  enabled ? "cursor-pointer hover:bg-slate-50" : "cursor-not-allowed bg-slate-50 text-slate-500",
                  tipo === service && "border-primary bg-primary/5")}>
                  <input type="radio" name="novo-servico" value={service} disabled={!enabled} checked={tipo === service}
                    onChange={() => setTipo(service)} className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" />
                  <span className="min-w-0">
                    <span className={cn("block font-medium", enabled && "text-slate-900")}>{SERVICE_LABELS[service]}</span>
                    {reason && <span className="block text-xs">{reason}</span>}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={create.isPending}>Cancelar</Button>
            <Button disabled={!tipo || create.isPending} onClick={() => {
              if (submitting.current) return;
              submitting.current = true;
              create.mutate();
            }}>
              {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Solicitar serviço
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
